import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WebSocketProvider } from "@/context/WebSocketContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { DisclaimerModal } from "@/components/DisclaimerModal";
import MainDashboard from "@/pages/MainDashboard";
import AIDashboard from "@/pages/AIDashboard";
import AITradeHistory from "@/pages/AITradeHistory";
import Dashboard from "@/pages/Dashboard";
import StockGrid from "@/pages/StockGrid";
import POCTest from "@/pages/POCTest";
import PerformanceAnalytics from "@/pages/PerformanceAnalytics";
import PredictionHistory from "@/pages/PredictionHistory";
import PredictionsGrid from "@/pages/PredictionsGrid";
import BatchJobs from "@/pages/BatchJobs";
import MachineLearning from "@/pages/MachineLearning";
import SimulationTrace from "@/pages/SimulationTrace";
import Login from "@/pages/Login";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

// Force Logout component - clears all storage and redirects to login
const ForceLogout = () => {
  React.useEffect(() => {
    // Clear all storage
    sessionStorage.clear();
    localStorage.removeItem("authToken");
    localStorage.removeItem("token");
    // Redirect to login
    window.location.href = "/login";
  }, []);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
    </div>
  );
};

// Protected Route wrapper with Disclaimer and Page Access Check
const ProtectedRoute = ({ children, requiredPath }) => {
  const { isAuthenticated, loading, hasPageAccess } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user has access to this page
  const pathToCheck = requiredPath || location.pathname;
  if (!hasPageAccess(pathToCheck)) {
    // Redirect to dashboard if user doesn't have access
    return <Navigate to="/" replace />;
  }
  
  // Show disclaimer after login, before accessing protected content
  return <DisclaimerModal>{children}</DisclaimerModal>;
};

// App Routes component (needs to be inside AuthProvider)
const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/logout" element={<ForceLogout />} />
      <Route path="/" element={<ProtectedRoute requiredPath="/"><MainDashboard /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute requiredPath="/dashboard"><AIDashboard /></ProtectedRoute>} />
      <Route path="/ai-history" element={<ProtectedRoute requiredPath="/ai-history"><AITradeHistory /></ProtectedRoute>} />
      <Route path="/legacy" element={<ProtectedRoute requiredPath="/legacy"><Dashboard /></ProtectedRoute>} />
      <Route path="/stocks" element={<ProtectedRoute requiredPath="/stocks"><StockGrid /></ProtectedRoute>} />
      <Route path="/predictions" element={<ProtectedRoute requiredPath="/predictions"><PredictionsGrid /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute requiredPath="/history"><PredictionHistory /></ProtectedRoute>} />
      <Route path="/performance" element={<ProtectedRoute requiredPath="/performance"><PerformanceAnalytics /></ProtectedRoute>} />
      <Route path="/batch-jobs" element={<ProtectedRoute requiredPath="/batch-jobs"><BatchJobs /></ProtectedRoute>} />
      <Route path="/machine-learning" element={<ProtectedRoute requiredPath="/machine-learning"><MachineLearning /></ProtectedRoute>} />
      <Route path="/simulation-trace" element={<ProtectedRoute requiredPath="/simulation-trace"><SimulationTrace /></ProtectedRoute>} />
      <Route path="/poc" element={<ProtectedRoute requiredPath="/poc"><POCTest /></ProtectedRoute>} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <WebSocketProvider>
              <AppRoutes />
            </WebSocketProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
